import React, {useCallback, useContext, useMemo, useState} from "react";
import {AppContext} from "../../context/app-context";
import {useSnackbar} from "../../context/snackbar-context";
import {SecurSpaceReportTitle} from "../../components/common";
import {SecurSpaceReportContainer} from "../../components/common";
import {TablePagination} from "@mui/material";
import {useBindSearchParam} from "../../hooks/useBindSearchParam";
import {useBindSearchQuery} from "../../hooks/useBindSearchQuery";
import {SortDirType} from "../../types/common/SortDirType";
import {useSnackbarDisplayOnError} from "../../hooks/error/useSnackbarDisplayOnError";
import {useAdminAccounts} from "../../hooks/admin/useAdminAccounts";
import {AccountCardListWithLoader} from "../../components/admin/account/AccountCardList";
import {useReportDataSummaryCollapseByIndex} from "../../hooks/report/useReportDataSummaryCollapseByIndex";
import DownloadCSVButton from "../../components/report/export/DownloadCSVButton";
import {styled} from "@mui/system";
import {
  changeAccountStatus,
  deleteAccount,
  editAccountType,
  getAccountReportDownload,
  RequestParams
} from "../../hooks/admin/admin-account-requests";
import AdminAccountReportDialogs from '../../components/admin/account/actions/AdminAccountReportDialogs';
import {defaultAccount, DialogType, dialogTypes} from "../../components/admin/account/actions/data";
import {UserType} from "../../components/constants/securspace-constants";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../util/NetworkErrorUtil";
import type {IFilterValues} from "./SortFilter/SortFilter";
import AdminAccountsSortFilter from "./SortFilter/SortFilterWithTextBuffer";
import useDebounce from "../../hooks/useDebounce";
import {PersonAndBoxMessage} from "../../components/messages/PersonAndBoxMessage";
import {AdminAccountReportVO} from "../../types/admin/AdminAccountReportVO";
import SecurSpaceContainer from "../../components/common/SecurSpaceContainer";
import {SecurSpaceReportPaginationContainer} from "../../components/common";

const DEFAULT_PAGE = 0;
const DEFAULT_ROWS_PER_PAGE = 10;

const CSVButtonParent = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  paddingBottom: '1rem'
})

const CSVButtonBox = styled('div')({
  width: '20%'
})

const AdminAccountReport = () => {

  const { snackbarShowMessage } = useSnackbar();
  const appContext = useContext(AppContext);
  const { user } = appContext;

  const userIsSuperAdmin = useMemo(() => user?.userType === UserType.SUPER_ADMIN, [user]);

  // Search params
  const [page, setPage] = useBindSearchParam('page', DEFAULT_PAGE.toString());
  const [rowsPerPage, setRowsPerPage] = useBindSearchParam('size', DEFAULT_ROWS_PER_PAGE.toString());
  const defaultFilterParams = useMemo(() => ({
    sortBy: 'COMPANY_NAME',
    sortDir: 'asc',
    creationDate: '',
    type: '',
    companyName: '',
    accountId: '',
    email: '',
    username: '',
    activated: ''
  }), []);

  // Replace true to avoid polluting history with filter changes
  const [filterParams, setFilterParams] = useBindSearchQuery(defaultFilterParams, { replace: true });

  // Search params -> sort filter values -> sort/filter component
  const filterValues = useMemo<IFilterValues>(() => ({
    companyName: String(filterParams.companyName || ''),
    accountId: String(filterParams.accountId || ''),
    activated: filterParams.activated === 'active' || filterParams.activated === 'inactive' ? filterParams.activated as IFilterValues['activated'] : '',
    type: String(filterParams.type || ''),
    email: String(filterParams.email || ''),
    username: String(filterParams.username || ''),
    creationDate: String(filterParams.creationDate || ''),
    sortBy: String(filterParams.sortBy || 'COMPANY_NAME'),
    sortDir: (filterParams.sortDir || 'asc') as SortDirType
  }), [filterParams]);

  // Debounced filter values
  const debouncedFilters = useDebounce<IFilterValues>(filterValues, 1000);

  // Query state for CSV download
  const csvQuery = useMemo<RequestParams>(() => ({
    page: page,
    size: rowsPerPage,
    sortBy: debouncedFilters.sortBy,
    sortDir: debouncedFilters.sortDir,
    creationDate: debouncedFilters.creationDate,
    type: debouncedFilters.type,
    accountId: debouncedFilters.accountId,
    email: debouncedFilters.email,
    username: debouncedFilters.username,
    activated: debouncedFilters.activated === '' ? '' : debouncedFilters.activated === 'active' ? 'true' : 'false',
  }), [page, rowsPerPage, debouncedFilters]);

  const defaultDialog = {open: false, type: dialogTypes.CHANGE_STATUS};

  const [openModal, setOpenModal] = useState<DialogType>(defaultDialog);

  const [accountBeingActioned, setAccountBeingActioned] = useState<AdminAccountReportVO>(defaultAccount);

  // TODO: need to reset page to 0 when number of pages < current page after filter change
  // Api hooks
  const {accounts, pageInfo, isLoading, error, doRequest} = useAdminAccounts({
    page: page,
    rowsPerPage: rowsPerPage,
    sort: debouncedFilters.sortBy,
    sortDir: debouncedFilters.sortDir,
    creationDate: debouncedFilters.creationDate,
    type: debouncedFilters.type,
    accountId: debouncedFilters.accountId,
    email: debouncedFilters.email,
    username: debouncedFilters.username,
    activated: debouncedFilters.activated === '' ? null : debouncedFilters.activated === 'active'
  });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage});

  // Handlers
  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage.toString());
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(event.target.value);
    setPage(DEFAULT_PAGE.toString());
  };

  const handleFilterChange = useCallback((filters: IFilterValues) => {
    // Clear company name when user type changes since company names are type-specific
    // @see {@link companyNameOptions} for fetching company names based on user type
    const typeChanged = filters.type !== filterValues.type;
    setFilterParams({
      companyName: typeChanged ? '' : filters.companyName,
      accountId: typeChanged ? '' : filters.accountId,
      type: filters.type,
      activated: filters.activated,
      email: filters.email,
      username: filters.username,
      creationDate: filters.creationDate,
      sortBy: filters.sortBy || 'COMPANY_NAME',
      sortDir: filters.sortDir || 'asc'
    });
  }, [filterValues.type, setFilterParams]);

  const {
    collapseByIndex,
    handleToggleCollapse,
  } = useReportDataSummaryCollapseByIndex({data: accounts});

  const onCloseDialog = () => {
    setOpenModal(defaultDialog);
    setAccountBeingActioned(defaultAccount);
  };

  const refreshPageAndCloseDialogs = () => {
    doRequest();
    onCloseDialog();
  };

  const onDeleteAction = (account: AdminAccountReportVO) => {
    const {id, companyName} = account;
    deleteAccount(id)
      .then(() => {
        snackbarShowMessage(`Successfully deleted account for ${companyName || id}`, 'success');
        refreshPageAndCloseDialogs();
      })
      .catch(err => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error'));
  };

  const onEditTypeAction = (account: AdminAccountReportVO) => {
    const {id} = account;
    editAccountType(id)
      .then(() => {
        snackbarShowMessage(`Successfully updated account type`, 'success');
        refreshPageAndCloseDialogs();
      })
      .catch(err => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error'))
  };

  const onChangeAction = (account: AdminAccountReportVO) => {
    const {id} = account;
    changeAccountStatus(id)
      .then(() => {
        snackbarShowMessage(`Successfully updated account status`, 'success');
        refreshPageAndCloseDialogs();
      })
      .catch(err => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error'));
  };

  // Left undefined until the first response (no `= []` default), so the empty
  // state can tell 'nothing loaded yet' apart from 'loaded, no accounts' —
  // the same distinction the other report views rely on.
  const accountHasLength = !!accounts?.length;

  return <SecurSpaceContainer>
    <SecurSpaceReportContainer>
      <SecurSpaceReportTitle>Accounts</SecurSpaceReportTitle>
      <AdminAccountsSortFilter
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
      />
      <CSVButtonParent>
        {
          accountHasLength &&
          <CSVButtonBox>
            <DownloadCSVButton
              className={'w-100'}
              title='Download Report'
              reportUrl={csvQuery ? getAccountReportDownload(csvQuery) : ''}
            />
          </CSVButtonBox>
        }
      </CSVButtonParent>
      {/* Mounted unconditionally and hidden via `visible`: rendering it only once
          accounts exist meant the loader HOC never saw the first load settle, so
          every refetch swapped the rows back for skeletons. */}
      <AccountCardListWithLoader visible={!error && accountHasLength}
                                 isLoading={isLoading}
                                 accounts={accounts}
                                 collapseByIndex={collapseByIndex}
                                 onToggleCollapse={handleToggleCollapse}
                                 setOpenModal={setOpenModal}
                                 isSuperAdmin={userIsSuperAdmin}
                                 setAccountBeingActioned={setAccountBeingActioned}
      />
      <AdminAccountReportDialogs
        openDialog={openModal}
        onCloseDialog={onCloseDialog}
        accountBeingActioned={accountBeingActioned}
        onEditTypeAction={onEditTypeAction}
        onChangeAction={onChangeAction}
        onDeleteAction={onDeleteAction}
      />
      <PersonAndBoxMessage visible={accounts?.length === 0 && !error}/>
      {
        accountHasLength && <SecurSpaceReportPaginationContainer>
          <TablePagination
            component={'div'}
            count={pageInfo?.count || 0}
            page={pageInfo?.page || DEFAULT_PAGE}
            onPageChange={handlePageChange}
            rowsPerPage={pageInfo?.rowsPerPage || DEFAULT_ROWS_PER_PAGE}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </SecurSpaceReportPaginationContainer>
      }
    </SecurSpaceReportContainer>
  </SecurSpaceContainer>;
}

export default AdminAccountReport;
